import { Injectable, NgZone } from '@angular/core';
import { BUILD_INFO } from '../../environments/build-info';

type BuildInfo = {
  version?: string;
  buildId?: string;
  commit?: string;
  branch?: string;
  buildNumber?: string;
  builtAt?: string;
};

@Injectable({ providedIn: 'root' })
export class AppVersionService {
  private readonly buildInfoUrl = 'assets/build-info.json';
  private readonly promptId = 'salesmax-version-update-prompt';
  private readonly checkIntervalMs = 5 * 60 * 1000;
  private readonly minCheckGapMs = 60 * 1000;
  private readonly snoozeMs = 10 * 60 * 1000;
  private started = false;
  private checkInFlight = false;
  private lastCheckStartedAt = 0;

  constructor(private zone: NgZone) {}

  start(): void {
    if (this.started || typeof window === 'undefined') {
      return;
    }

    this.started = true;
    (window as Window & { __BUILD?: BuildInfo }).__BUILD = BUILD_INFO;
    this.installGlobalErrorHandlers();

    this.zone.runOutsideAngular(() => {
      window.setTimeout(() => this.requestCheck(), 30000);
      window.setInterval(() => this.requestCheck(), this.checkIntervalMs);
      window.addEventListener('focus', () => this.requestCheck());
      window.addEventListener('online', () => this.requestCheck());
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) {
          this.requestCheck();
        }
      });
    });
  }

  isStaleBuildError(error: unknown): boolean {
    const message = this.errorText(error);
    return /ChunkLoadError|Loading chunk [\w-]+ failed|Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS|Loading CSS chunk/i.test(message);
  }

  showReloadPrompt(reason: 'new-version' | 'chunk-error' = 'new-version', latest?: BuildInfo): void {
    if (typeof document === 'undefined' || document.getElementById(this.promptId)) {
      return;
    }

    const latestBuildId = latest?.buildId || 'unknown';
    const snoozeKey = `salesmax-version-snooze:${latestBuildId}`;
    if (reason === 'new-version' && Number(sessionStorage.getItem(snoozeKey) || 0) > Date.now()) {
      return;
    }

    console.info('[AppVersion] showing reload prompt:', reason, latestBuildId);

    const prompt = document.createElement('div');
    prompt.id = this.promptId;
    prompt.setAttribute('role', 'alert');
    prompt.style.cssText = [
      'position:fixed',
      'right:20px',
      'bottom:20px',
      'z-index:2147483647',
      'max-width:380px',
      'padding:16px',
      'border-radius:8px',
      'box-shadow:0 12px 36px rgba(0,0,0,.24)',
      'background:#1f2937',
      'color:#fff',
      'font:14px/1.45 -apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
    ].join(';');

    const title = document.createElement('div');
    title.textContent = reason === 'chunk-error' ? 'Salesmax was updated' : 'New Salesmax version available';
    title.style.cssText = 'font-weight:600;margin-bottom:4px';

    const body = document.createElement('div');
    body.textContent = reason === 'chunk-error'
      ? 'This page is running an older build and cannot load the latest files. Reload to continue.'
      : 'Reload when convenient to switch to the latest build.';
    body.style.cssText = 'margin-bottom:12px;color:#d1d5db';

    const actions = document.createElement('div');
    actions.style.cssText = 'display:flex;gap:8px;justify-content:flex-end';

    if (reason === 'new-version') {
      const later = document.createElement('button');
      later.type = 'button';
      later.textContent = 'Later';
      later.style.cssText = this.buttonStyle('transparent', '#fff', 'rgba(255,255,255,.24)');
      later.onclick = () => {
        sessionStorage.setItem(snoozeKey, String(Date.now() + this.snoozeMs));
        prompt.remove();
      };
      actions.appendChild(later);
    }

    const reload = document.createElement('button');
    reload.type = 'button';
    reload.textContent = 'Reload';
    reload.style.cssText = this.buttonStyle('#fff', '#111827', '#fff');
    reload.onclick = () => window.location.reload();
    actions.appendChild(reload);

    prompt.appendChild(title);
    prompt.appendChild(body);
    prompt.appendChild(actions);
    document.body.appendChild(prompt);
  }

  private requestCheck(): void {
    if (typeof document !== 'undefined' && document.hidden) {
      return;
    }

    if (this.checkInFlight) {
      return;
    }

    const now = Date.now();
    if (now - this.lastCheckStartedAt < this.minCheckGapMs) {
      return;
    }

    this.lastCheckStartedAt = now;
    this.checkInFlight = true;
    void this.checkForUpdate().finally(() => {
      this.checkInFlight = false;
    });
  }

  private async checkForUpdate(): Promise<void> {
    try {
      const response = await fetch(`${this.buildInfoUrl}?t=${Date.now()}`, {
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache' },
      });

      if (!response.ok) {
        return;
      }

      const latest = await response.json() as BuildInfo;
      if (latest?.buildId && latest.buildId !== BUILD_INFO.buildId) {
        this.showReloadPrompt('new-version', latest);
      }
    } catch (_err) {
      // Network failures should not interrupt user work.
    }
  }

  private installGlobalErrorHandlers(): void {
    window.addEventListener('error', (event) => {
      if (this.isStaleBuildError(event.error || event.message)) {
        event.preventDefault();
        this.showReloadPrompt('chunk-error');
      }
    });

    window.addEventListener('unhandledrejection', (event) => {
      if (this.isStaleBuildError(event.reason)) {
        event.preventDefault();
        this.showReloadPrompt('chunk-error');
      }
    });
  }

  private errorText(error: unknown, seen = new WeakSet<object>()): string {
    if (!error) {
      return '';
    }

    if (typeof error === 'string') {
      return error;
    }

    if (typeof error !== 'object') {
      return String(error);
    }

    if (seen.has(error)) {
      return '';
    }

    seen.add(error);

    const anyError = error as { message?: unknown; stack?: unknown; reason?: unknown; rejection?: unknown };
    return [
      anyError.message,
      anyError.stack,
      this.errorText(anyError.reason, seen),
      this.errorText(anyError.rejection, seen),
    ].filter(Boolean).join(' ');
  }

  private buttonStyle(background: string, color: string, border: string): string {
    return [
      'appearance:none',
      `background:${background}`,
      `border:1px solid ${border}`,
      'border-radius:6px',
      `color:${color}`,
      'cursor:pointer',
      'font:600 13px -apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
      'padding:7px 12px',
    ].join(';');
  }
}
